/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const CheckoutContext = React.createContext()

export const CheckoutProvider = ({children}) => {
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()
    const [step, setStep] = useState(null)
    const [previousStep, setPreviousStep] = useState(null)
    const [dialCountry, setDialCountry] = useState(null)
    const {locale} = useMultiSite()
    const userNavigatedRef = useRef(false)

    const CHECKOUT_STEPS_LIST = [
        'CONTACT_INFO',
        'SHIPPING_ADDRESS',
        'SHIPPING_OPTIONS',
        'PAYMENT',
        'ADYEN',
        'ZEROBALLANCE',
        'CUSTOM_PAYMENT'
    ]
    const STEPS = CHECKOUT_STEPS_LIST.reduce((acc, step, idx) => ({...acc, [step]: idx}), {})
    const currentCountryCode = locale?.id?.split('-')?.[1]

    useEffect(() => {
        if (!customer || !basket) return
        const getNextStep = () => {
            const shippingAddress = basket?.shipments?.[0]?.shippingAddress
            if (
                !shippingAddress ||
                !shippingAddress?.c_ID ||
                (currentCountryCode && shippingAddress?.countryCode !== currentCountryCode)
            ) {
                return STEPS.SHIPPING_ADDRESS
            }
            if (
                !basket.shipments?.[0]?.shippingMethod &&
                (!basket.c_deliveryDateMax || !basket.c_deliveryDateMin)
            ) {
                return STEPS.SHIPPING_OPTIONS
            }
            if (
                !basket.billingAddress ||
                !basket.billingAddress?.c_ID ||
                basket.billingAddress?.countryCode !== shippingAddress?.countryCode
            ) {
                return STEPS.PAYMENT
            }
            if (
                basket?.orderTotal === 0 &&
                basket?.productItems &&
                basket?.productItems?.length > 0
            ) {
                return STEPS.ZEROBALLANCE
            }
            if (
                basket?.paymentInstruments?.[0]?.paymentMethodId === 'payment30days' ||
                basket?.paymentInstruments?.[0]?.paymentMethodId === 'payment4installments'
            ) {
                return STEPS.CUSTOM_PAYMENT
            }
            return STEPS.ADYEN
        }
        const requiredStep = getNextStep()

        if (step === null) {
            setStep(requiredStep)
            return
        }

        if (userNavigatedRef.current && step < requiredStep) {
            return
        }

        if (userNavigatedRef.current && step > requiredStep) {
            setStep(requiredStep)
            userNavigatedRef.current = false
            return
        }

        if (!userNavigatedRef.current && requiredStep > step) {
            setStep(requiredStep)
            return
        }

        if (requiredStep < step && !userNavigatedRef.current) {
            setStep(requiredStep)
        }
    }, [
        basket?.productItems,
        basket?.shipments?.[0]?.shippingAddress?.c_ID,
        basket?.billingAddress?.c_ID,
        basket?.billingAddress?.countryCode,
        basket?.shipments?.[0]?.shippingMethod,
        basket?.paymentInstruments,
        basket?.billingAddress,
        basket?.c_deliveryDateMin,
        basket?.c_deliveryDateMax,
        basket?.orderTotal,
        customer,
        basket,
        basket?.shipments?.[0]?.shippingAddress,
        basket?.basketId
    ])

    const value = {
        step,
        STEPS,
        goToNextStep: () => {
            userNavigatedRef.current = true
            if (step > 1) setPreviousStep(step)
            setStep(step + 1)
        },
        goToStep: (newStep) => {
            userNavigatedRef.current = true
            if (step > 1) setPreviousStep(step)
            setStep(newStep)
        },
        dialCountry,
        previousStep,
        setDialCountry
    }

    if (step === null) {
        return null
    }

    return <CheckoutContext.Provider value={value}>{children}</CheckoutContext.Provider>
}

CheckoutProvider.propTypes = {
    children: PropTypes.any
}

/**
 * A hook for managing checkout state and actions
 * @returns {Object} Checkout data and actions
 */
export const useCheckout = () => {
    return React.useContext(CheckoutContext)
}
